/* eslint-disable camelcase */
import { Module } from 'vuex'
import { RootState } from '../types'
import api from '@/api/axios'

export interface AccountStats {
  properties_count: number
  units_count: number
  occupied_units: number
  vacant_units: number
  tenants_count: number
  active_tenants: number
  total_invoiced: number
  total_collected: number
  outstanding_balance: number
  overdue_invoices_count: number
  total_expenses: number
  net_profit: number
}

export interface PlatformAccount {
  id: number
  name: string
  email: string
  phone?: string
  account_type: 'Landlord' | 'Agency'
  status: 'active' | 'suspended'
  created_at: string
  last_login_at: string | null
  suspension_reason?: string | null
  suspended_at?: string | null
  stats: AccountStats
}

export interface AdminProperty {
  id: number
  name: string
  address: string
  type: string
  total_units: number
  occupied_units: number
  vacant_units: number
  monthly_income: number
  created_at: string
}

export interface AdminInvoice {
  id: number
  invoice_number: string
  tenant_name: string
  unit_name: string
  property_name: string
  amount: number
  due_date: string
  status: 'paid' | 'unpaid' | 'overdue'
  paid_at: string | null
}

export interface AdminPayment {
  id: number
  date: string
  tenant_name: string
  invoice_number: string
  property_name: string
  amount: number
  method: string
}

export interface PlatformStats {
  total_accounts: number
  active_accounts: number
  suspended_accounts: number
  landlord_accounts: number
  agency_accounts: number
  total_properties: number
  total_units: number
  total_tenants: number
  total_revenue_this_month: number
  new_accounts_this_month: number
}

export interface AdminAccountsState {
  list: PlatformAccount[]
  current: PlatformAccount | null
  properties: AdminProperty[]
  invoices: AdminInvoice[]
  payments: AdminPayment[]
  platformStats: PlatformStats | null
  loading: boolean
  detailLoading: boolean
  total: number
  currentPage: number
}

const adminAccounts: Module<AdminAccountsState, RootState> = {
  namespaced: true,

  state: (): AdminAccountsState => ({
    list: [],
    current: null,
    properties: [],
    invoices: [],
    payments: [],
    platformStats: null,
    loading: false,
    detailLoading: false,
    total: 0,
    currentPage: 1
  }),

  getters: {
    list: (state) => state.list,
    current: (state) => state.current,
    properties: (state) => state.properties,
    invoices: (state) => state.invoices,
    payments: (state) => state.payments,
    platformStats: (state) => state.platformStats,
    loading: (state) => state.loading,
    detailLoading: (state) => state.detailLoading,
    total: (state) => state.total,
    currentPage: (state) => state.currentPage
  },

  mutations: {
    SET_LIST (state, { data, total, page }: { data: PlatformAccount[]; total: number; page: number }) {
      state.list = data
      state.total = total
      state.currentPage = page
    },
    SET_CURRENT (state, account: PlatformAccount) { state.current = account },
    SET_PROPERTIES (state, props: AdminProperty[]) { state.properties = props },
    SET_INVOICES (state, invoices: AdminInvoice[]) { state.invoices = invoices },
    SET_PAYMENTS (state, payments: AdminPayment[]) { state.payments = payments },
    SET_PLATFORM_STATS (state, stats: PlatformStats) { state.platformStats = stats },
    SET_LOADING (state, val: boolean) { state.loading = val },
    SET_DETAIL_LOADING (state, val: boolean) { state.detailLoading = val },
    UPDATE_STATUS (state, { id, status, suspension_reason, suspended_at }: { id: number; status: string; suspension_reason: string | null; suspended_at: string | null }) {
      const account = state.list.find(a => a.id === id)
      if (account) {
        account.status = status as 'active' | 'suspended'
        account.suspension_reason = suspension_reason
        account.suspended_at = suspended_at
      }
      if (state.current && state.current.id === id) {
        state.current.status = status as 'active' | 'suspended'
        state.current.suspension_reason = suspension_reason
        state.current.suspended_at = suspended_at
      }
    },
    RESET_DETAIL (state) {
      state.current = null
      state.properties = []
      state.invoices = []
      state.payments = []
    }
  },

  actions: {
    async fetchAll ({ commit }, params?: { search?: string; account_type?: string; status?: string; page?: number }) {
      commit('SET_LOADING', true)
      try {
        const { data } = await api.get('/admin/accounts', { params })
        commit('SET_LIST', {
          data: data.data ?? data,
          total: data.meta?.total ?? (data.data ?? data).length,
          page: data.meta?.current_page ?? 1
        })
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchOne ({ commit }, id: number) {
      commit('SET_DETAIL_LOADING', true)
      commit('RESET_DETAIL')
      try {
        const { data } = await api.get(`/admin/accounts/${id}`)
        commit('SET_CURRENT', data.data ?? data)
      } finally {
        commit('SET_DETAIL_LOADING', false)
      }
    },

    async fetchProperties ({ commit }, id: number) {
      const { data } = await api.get(`/admin/accounts/${id}/properties`)
      commit('SET_PROPERTIES', data.data ?? data)
    },

    async fetchInvoices ({ commit }, id: number) {
      const { data } = await api.get(`/admin/accounts/${id}/invoices`)
      commit('SET_INVOICES', data.data ?? data)
    },

    async fetchPayments ({ commit }, id: number) {
      const { data } = await api.get(`/admin/accounts/${id}/payments`)
      commit('SET_PAYMENTS', data.data ?? data)
    },

    async suspend ({ commit }, { id, reason }: { id: number; reason: string }) {
      const { data } = await api.patch(`/admin/accounts/${id}/suspend`, { reason })
      commit('UPDATE_STATUS', {
        id,
        status: 'suspended',
        suspension_reason: data.suspension_reason ?? reason,
        suspended_at: data.suspended_at ?? new Date().toISOString()
      })
      return data
    },

    async activate ({ commit }, id: number) {
      const { data } = await api.patch(`/admin/accounts/${id}/activate`)
      commit('UPDATE_STATUS', { id, status: 'active', suspension_reason: null, suspended_at: null })
      return data
    },

    async fetchPlatformStats ({ commit }) {
      const { data } = await api.get('/admin/stats')
      commit('SET_PLATFORM_STATS', data.data ?? data)
    }
  }
}

export default adminAccounts
